import { Editor } from 'components/Editor/Editor'
import { EditorRHF } from 'components/Editor/EditorRHF'
import FileContainer from 'components/FileContainer/FileContainer'
import { useRef } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { CgAttachment } from 'react-icons/cg'
import { MdClose } from 'react-icons/md'
import { toast } from 'react-toastify'
import { API_HOST } from 'utils/http-client'
import { AutomationAttachment } from 'models/automation'
import useApiService from 'services/api'

const ALLOWED_FILE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'svg',
  'webp',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
]
const ALLOWED_FILE_SIZE = 4 * 1024 * 1024

// Union type for form attachments - can be new files or existing attachments
export type FormAttachment = File | AutomationAttachment

interface AutomationNoteManagerProps<T extends FieldValues> {
  control: Control<T>
  noteName: Path<T>
  attachmentsName: Path<T>
  label?: string
  placeholder?: string
  viewMode?: boolean
  editorClassName?: string
  enableMentions?: boolean
  automationId?: number // For deleting existing attachments
}

const AttachmentContainer = ({
  file,
  handleRemoveFile,
  viewMode,
  href,
}: {
  file: { name: string }
  handleRemoveFile?: () => void
  viewMode?: boolean
  href?: string
}) => {
  return (
    <FileContainer
      file={file}
      RemoveFileIcon={viewMode ? undefined : MdClose}
      noOutline
      handleRemoveFile={handleRemoveFile}
      href={href}
    />
  )
}

export const AutomationNoteManager = <T extends FieldValues>(
  props: AutomationNoteManagerProps<T>
) => {
  const {
    control,
    noteName,
    attachmentsName,
    label,
    placeholder,
    viewMode,
    editorClassName = 'h-28',
    enableMentions = false,
    automationId,
  } = {
    placeholder: 'Start writing your note here',
    label: 'New Note',
    ...props,
  }

  const api = useApiService()

  const {
    field: { onChange: attachmentsOnChange, value: attachmentsValue },
  } = useController({ name: attachmentsName, control }) as {
    field: {
      onChange: (value: FormAttachment[]) => void
      value: FormAttachment[]
    }
  }

  const inputRef = useRef<HTMLInputElement>(null)

  const openFileSelect = () => {
    inputRef.current?.click()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const files = e.target.files
    if (!files) return
    const filesArr = Array.from(files)
    const invalidFilesExtensions = filesArr.filter(
      (file) =>
        !ALLOWED_FILE_EXTENSIONS.includes(
          file.name.split('.').pop()?.toLowerCase() || ''
        )
    )
    if (invalidFilesExtensions.length > 0) {
      toast.error(
        `Only the following file types are allowed: ${ALLOWED_FILE_EXTENSIONS.join(
          ', '
        )}`
      )
      return
    }
    const invalidFilesSize = filesArr.filter(
      (file) => file.size > ALLOWED_FILE_SIZE
    )
    if (invalidFilesSize.length > 0) {
      toast.error(
        `The maximum file size allowed is ${ALLOWED_FILE_SIZE / 1024 / 1024}MB`
      )
      return
    }
    attachmentsOnChange([...(attachmentsValue || []), ...filesArr])
    e.target.value = '' // reset input value to allow re-selecting the same file
  }

  const handleRemoveAttachment = async (index: number) => {
    const attachment = attachmentsValue?.[index]

    // If it's an existing attachment (not a File), delete it via API
    if (
      attachment &&
      !(attachment instanceof File) &&
      attachment.id &&
      automationId
    ) {
      try {
        await api.deleteAutomationAttachment(automationId, attachment.id)
        toast.success('Attachment removed successfully')
      } catch (error) {
        toast.error('Failed to remove attachment')
        return // Don't remove from form if API call failed
      }
    }

    // Remove from form state
    const newAttachments = (attachmentsValue || []).filter(
      (_, i) => i !== index
    )
    attachmentsOnChange(newAttachments)
  }

  // Helper function to get display name for different attachment types
  const getAttachmentDisplayName = (attachment: FormAttachment): string => {
    if (attachment instanceof File) {
      return attachment.name
    }
    // For AutomationAttachment objects
    return attachment.original_filename || 'Unknown file'
  }

  // Helper function to get href for existing attachments
  const getAttachmentHref = (
    attachment: FormAttachment
  ): string | undefined => {
    if (attachment instanceof File) {
      return undefined // New files don't have URLs yet
    }
    // For AutomationAttachment objects
    const file = attachment.file
    return file?.startsWith('/') ? `${API_HOST}${file}` : file
  }

  return (
    <div>
      {viewMode ? (
        <Editor
          value="" // Automation content comes from the form, not a separate note
          readOnly
          disableToolbar
          borderless
        />
      ) : (
        <EditorRHF
          name={noteName}
          className={editorClassName}
          control={control}
          label={label}
          placeholder={placeholder}
          enableMentions={enableMentions}
          additionalToolbarButtons={[
            <CgAttachment
              key="attachments"
              size={24}
              className="cursor-pointer p-1 w-8 hover:text-brand-active"
              onClick={openFileSelect}
            />,
          ]}
        />
      )}

      <div className="mt-1 gap-1 flex flex-column">
        {!viewMode &&
          attachmentsValue?.map((attachment: FormAttachment, index) => (
            <AttachmentContainer
              key={`attachment-${index}`}
              file={{ name: getAttachmentDisplayName(attachment) }}
              handleRemoveFile={() => handleRemoveAttachment(index)}
              href={getAttachmentHref(attachment)}
            />
          ))}
      </div>

      {!viewMode && (
        <input
          type="file"
          hidden
          multiple
          ref={inputRef}
          onChange={handleFileSelect}
          accept={ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
        />
      )}
    </div>
  )
}
